import { useMemo } from 'react';
import FormattedMessage from 'I18n/components/FormattedMessage';
import LineItems from '../LineItems/LineItems';
import FutureCharges from '../FutureCharges/FutureCharges';
import styled from 'styled-components';
import Total from '../Total/Total';
import DiscountCode from '../Adjustments/DiscountCode';
import { canReadText, getWhiteness } from '../../utils/colors';
import { SubtotalAdjustments } from '../Adjustments/SubtotalAdjustments';
import { useLineItemErrors } from '../../hooks/useLineItemErrors';
import { OPEN, SUBMITTED } from '../../constants/Statuses';
import { useOptionalStates } from '../../hooks/OrderSummary/useOptionalStates';
import { sortLineItemArrays } from '../../utils/lineItem';
import { isAtCheckoutLineItem } from '../../types/utils/typeguards';
import partition from 'hs-lodash/partition';
import { getIsZeroCheckoutValid } from '../../utils/validations';
import { useSubtotals } from '../../context/SubtotalsProvider';
import useApplyDiscountCodeFromResponse from '../../hooks/DiscountCode/useApplyDiscountCodeFromResponse';
import { getLineItemBlurs } from '../../state/lineItemBlursSlice';
import { useSelector } from 'react-redux';
import { getShowAllErrors } from '../../state/errorMessagesSlice';
import { getCalculateTaxLoading } from '../../state/calculateTaxSlice';
import { LoadingSpinner } from 'foundations-components/transitional/components/LoadingSpinner';
import { Flex } from 'foundations-components/components/Flex';
import { CardSection, CardWrapper } from 'foundations-components/transitional/components/Card';
import { Text } from 'foundations-components/components/Text';
import { HR } from 'foundations-components/transitional/components/HR';
import { Link } from 'foundations-components/transitional/components/Link';
import { useTheme } from 'foundations-theming/utils';
import { DownloadIcon } from 'foundations-assets/icons/components/DownloadIcon';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const StyledDiv = styled.div.withConfig({
  displayName: "OrderSummary__StyledDiv"
})(["", ""], ({
  primaryColor,
  theme
}) => {
  const backgroundColor = primaryColor;
  const borderBottomColor = getWhiteness(primaryColor) ? theme.color['border-core-subtle'] : primaryColor;
  return `
      background-color: ${backgroundColor};
      border-bottom: 1px solid ${borderBottomColor};
  `;
});
const OrderSummary = ({
  checkoutSession
}) => {
  const {
    status,
    currencyCode,
    userDefinedBranding,
    isDiscountCodeEnabled,
    lineItems,
    futureContractLineItems = [],
    invoiceCheckoutInfo
  } = checkoutSession;
  const theme = useTheme();
  const primaryColor = (userDefinedBranding === null || userDefinedBranding === void 0 ? void 0 : userDefinedBranding.primaryColor) || theme.color['fill-accent-neutral-default'];
  const headerTextColorToken = canReadText(primaryColor, theme) ? 'text-primary-default' : 'text-core-default';
  const isOnDarkBackground = headerTextColorToken === 'text-primary-default';
  const taxCalculationLoading = useSelector(getCalculateTaxLoading);
  const {
    discountCodeResponse,
    oneTimeDiscountAmount,
    adjustmentAmounts
  } = useSubtotals();
  const sessionOpen = status === OPEN;
  const readOnly = !sessionOpen;
  const checkoutFeeAdjustment = useMemo(() => adjustmentAmounts.find(adjustment => adjustment.adjustmentType === 'CHECKOUT_FEE'), [adjustmentAmounts]);
  useApplyDiscountCodeFromResponse({
    discountCodeResponse,
    oneTimeDiscountAmount
  });
  const [billedAtCheckoutLineItems, futureStartDateLineItems] = partition(lineItems, isAtCheckoutLineItem);
  const isZeroCheckout = getIsZeroCheckoutValid(checkoutSession.subtotalsPublicResponse && checkoutSession.subtotalsPublicResponse.paymentAmount, lineItems);
  const {
    selectedItems,
    selectionToggles
  } = useOptionalStates(billedAtCheckoutLineItems);
  const lineItemBlurs = useSelector(getLineItemBlurs);
  const showAllErrors = useSelector(getShowAllErrors);
  const {
    selectedItems: futureSelectedItems,
    selectionToggles: futureSelectionToggles
  } = useOptionalStates(futureStartDateLineItems);
  const {
    selectedItems: contractSelectedItems,
    selectionToggles: contractSelectionToggles
  } = useOptionalStates(futureContractLineItems);
  const billedAtCheckoutLineItemErrors = useLineItemErrors({
    lineItems: billedAtCheckoutLineItems,
    blurStates: lineItemBlurs,
    showAllErrors,
    readOnly,
    selectedItems
  });
  const futureStartDateLineItemErrors = useLineItemErrors({
    lineItems: futureStartDateLineItems,
    blurStates: lineItemBlurs,
    showAllErrors,
    readOnly,
    selectedItems: futureSelectedItems
  });
  const contractLineItemErrors = useLineItemErrors({
    lineItems: futureContractLineItems,
    blurStates: lineItemBlurs,
    showAllErrors,
    readOnly,
    selectedItems: contractSelectedItems
  });

  /**
   * @note 1: after we submit payment, we have to filter out the unselected
   * optional line items while BE asynchronously processes the payment
   * https://hubspot.slack.com/archives/C04D60WK0TU/p1736367641360299
   *
   * @note 2: for line items that aren't optional, selectedItems[index] will always be null.
   * similarly, for all optional line items selectedItems[index] will always be true or false
   * https://git.hubteam.com/HubSpotProtected/payments-ui/blob/master/payments-ui-app/static-1.27841/js/hooks/OrderSummary/useOptionalStates.ts#L24
   */
  const selectedBilledAtCheckoutLineItems = billedAtCheckoutLineItems.filter((_item, index) => selectedItems[index] === true || selectedItems[index] === null);
  const selectedFutureStartDateLineItems = futureStartDateLineItems.filter((_item, index) => futureSelectedItems[index] === true || futureSelectedItems[index] === null);
  const selectedContractLineItems = futureContractLineItems.filter((_item, index) => contractSelectedItems[index] === true || contractSelectedItems[index] === null);
  const [sortedItems, sortedToggles, sortedErrors, sortedSelections] = sortLineItemArrays({
    lineItems: status === SUBMITTED ? selectedBilledAtCheckoutLineItems : billedAtCheckoutLineItems,
    selectionToggles,
    lineItemErrors: billedAtCheckoutLineItemErrors,
    selectedItems
  });
  const [sortedFutureItems, sortedFutureToggles, sortedFutureErrors, sortedFutureSelections] = sortLineItemArrays({
    lineItems: status === SUBMITTED ? selectedFutureStartDateLineItems : futureStartDateLineItems,
    selectionToggles: futureSelectionToggles,
    lineItemErrors: futureStartDateLineItemErrors,
    selectedItems: futureSelectedItems
  });
  const [sortedContractItems, sortedContractToggles, sortedContractErrors, sortedContractSelections] = sortLineItemArrays({
    lineItems: status === SUBMITTED ? selectedContractLineItems : futureContractLineItems,
    selectionToggles: contractSelectionToggles,
    lineItemErrors: contractLineItemErrors,
    selectedItems: contractSelectedItems
  });
  const hasFutureBilledItems = sortedFutureItems.length > 0;
  const hasFutureContractCharges = sortedContractItems.length > 0;
  const hasFutureCharges = hasFutureBilledItems || hasFutureContractCharges;
  const maybeFutureCharges = useMemo(() => hasFutureBilledItems && /*#__PURE__*/_jsx(FutureCharges, {
    futureStartDateLineItems: sortedFutureItems,
    currencyCode: currencyCode,
    lineItemErrors: sortedFutureErrors,
    readOnly: readOnly,
    selectionToggles: sortedFutureToggles,
    selectedItems: sortedFutureSelections,
    sessionOpen: sessionOpen,
    isZeroCheckout: isZeroCheckout,
    checkoutFeeAdjustment: checkoutFeeAdjustment
  }), [hasFutureBilledItems, sortedFutureItems, currencyCode, sortedFutureErrors, readOnly, sortedFutureToggles, sortedFutureSelections, sessionOpen, isZeroCheckout, checkoutFeeAdjustment]);
  const maybeFutureContractCharges = useMemo(() => hasFutureContractCharges && /*#__PURE__*/_jsx(FutureCharges, {
    futureStartDateLineItems: sortedContractItems,
    currencyCode: currencyCode,
    lineItemErrors: sortedContractErrors,
    readOnly: readOnly,
    selectionToggles: sortedContractToggles,
    selectedItems: sortedContractSelections,
    sessionOpen: sessionOpen,
    isZeroCheckout: isZeroCheckout,
    checkoutFeeAdjustment: checkoutFeeAdjustment,
    isContractSection: true
  }), [hasFutureContractCharges, sortedContractItems, currencyCode, sortedContractErrors, readOnly, sortedContractToggles, sortedContractSelections, sessionOpen, isZeroCheckout, checkoutFeeAdjustment]);
  return /*#__PURE__*/_jsxs(CardWrapper, {
    marginBlockEnd: "700",
    children: [/*#__PURE__*/_jsx(StyledDiv, {
      primaryColor: primaryColor,
      children: /*#__PURE__*/_jsxs(Flex, {
        justifyContent: "space-between",
        paddingBlockStart: "300",
        paddingBlockEnd: "100",
        paddingInline: "600",
        children: [/*#__PURE__*/_jsx(Text, {
          color: headerTextColorToken,
          paddingBlockEnd: "250",
          typography: "heading-100",
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: invoiceCheckoutInfo ? 'payments.invoiceSummary.header' : 'payments.orderSummary.header'
          })
        }), invoiceCheckoutInfo && /*#__PURE__*/_jsxs(Flex, {
          gap: "300",
          children: [/*#__PURE__*/_jsx(Link, {
            use: isOnDarkBackground ? 'on-fill' : 'on-bright',
            href: invoiceCheckoutInfo.viewInvoiceUrl,
            external: true,
            children: /*#__PURE__*/_jsx(FormattedMessage, {
              message: "payments.invoiceSummary.view"
            })
          }), /*#__PURE__*/_jsxs(Link, {
            use: isOnDarkBackground ? 'on-fill' : 'on-bright',
            href: invoiceCheckoutInfo.downloadInvoiceUrl,
            children: [/*#__PURE__*/_jsx(FormattedMessage, {
              message: "payments.invoiceSummary.download"
            }), /*#__PURE__*/_jsx("span", {
              style: {
                marginInlineStart: theme.space['100'],
                verticalAlign: 'middle'
              },
              children: /*#__PURE__*/_jsx(DownloadIcon, {})
            })]
          })]
        })]
      })
    }), taxCalculationLoading && /*#__PURE__*/_jsxs(Flex, {
      "data-test-id": "tax-calculation-loading",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      blockSize: "350px",
      children: [/*#__PURE__*/_jsx(LoadingSpinner, {}), /*#__PURE__*/_jsx(FormattedMessage, {
        message: "payments.orderSummary.calculatingTax"
      })]
    }), /*#__PURE__*/_jsxs(CardSection, {
      padding: "0",
      display: taxCalculationLoading ? 'none' : 'block',
      children: [/*#__PURE__*/_jsx(LineItems, {
        lineItems: sortedItems,
        currencyCode: currencyCode,
        lineItemErrors: sortedErrors,
        readOnly: readOnly,
        selectionToggles: sortedToggles,
        selectedItems: sortedSelections,
        sessionOpen: sessionOpen
      }), /*#__PURE__*/_jsx(HR, {
        distance: "medium",
        marginBlockStart: "0"
      }), isDiscountCodeEnabled && /*#__PURE__*/_jsx(DiscountCode, {
        checkoutSession: checkoutSession
      }), /*#__PURE__*/_jsx(SubtotalAdjustments, {
        session: checkoutSession
      }), /*#__PURE__*/_jsx(Total, {
        hasFutureCharges: hasFutureCharges,
        checkoutSession: checkoutSession,
        selectedOptionalLineItems: selectedItems
      })]
    }), maybeFutureCharges, maybeFutureContractCharges]
  });
};
export default OrderSummary;